import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Send, Users, Activity, Brain, Video, MessageSquare,
  Plus, Clock, CheckCircle, AlertCircle, ChevronRight, RefreshCw,
  ClipboardList, Package, Stethoscope, Calendar, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhysicianPortalEnhanced = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientInsights, setPatientInsights] = useState(null);
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [loading, setLoading] = useState(true);

  // New Referral Form
  const [newReferral, setNewReferral] = useState({
    patient_first_name: '',
    patient_last_name: '',
    patient_dob: '',
    patient_phone: '',
    primary_diagnosis: '',
    urgency: 'routine',
    notes: ''
  });

  // New Order Form
  const [newOrder, setNewOrder] = useState({
    patient_id: '',
    patient_name: '',
    order_type: 'medication',
    priority: 'routine',
    items: [],
    instructions: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, referralsRes, ordersRes, patientsRes, messagesRes] = await Promise.all([
        fetch(`${API_URL}/api/phase35/stats/physician`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/phase35/referrals/my`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/phase35/orders/my`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/phase35/patients/referred`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/phase35/messages/inbox`, { headers: getAuthHeaders() })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (referralsRes.ok) {
        const data = await referralsRes.json();
        setReferrals(data.referrals || []);
      }
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setOrders(data.orders || []);
      }
      if (patientsRes.ok) {
        const data = await patientsRes.json();
        setPatients(data.patients || []);
      }
      if (messagesRes.ok) {
        const data = await messagesRes.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const createReferral = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase35/referrals/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newReferral)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Referral ${data.referral_number} submitted successfully`);
        setShowNewReferral(false);
        setNewReferral({
          patient_first_name: '',
          patient_last_name: '',
          patient_dob: '',
          patient_phone: '',
          primary_diagnosis: '',
          urgency: 'routine',
          notes: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create referral');
      }
    } catch (error) {
      toast.error('Error creating referral');
    }
  };

  const createOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase35/orders/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newOrder)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Order ${data.order_number} created successfully`);
        setShowNewOrder(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Error creating order');
    }
  };

  const generateOrdersFromCarePlan = async (patientId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase35/orders/generate-from-care-plan?patient_id=${patientId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Generated ${data.total_orders} order(s) from care plan`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate orders');
      }
    } catch (error) {
      toast.error('Error generating orders');
    }
  };

  const fetchPatientInsights = async (patientId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase35/patients/${patientId}/ai-insights`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setPatientInsights(await response.json());
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': case 'completed': return 'bg-green-100 text-green-700';
      case 'rejected': case 'cancelled': return 'bg-red-100 text-red-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="physician-portal-enhanced">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Stethoscope className="h-7 w-7 text-teal-600" />
            Physician Portal
          </h1>
          <p className="text-gray-600 mt-1">Manage referrals, orders, and patient care coordination</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Referrals</p>
                <p className="text-2xl font-bold text-teal-600">{stats?.referrals?.total || 0}</p>
                <p className="text-xs text-yellow-600">{stats?.referrals?.pending || 0} pending</p>
              </div>
              <FileText className="h-8 w-8 text-teal-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Orders</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.orders?.total || 0}</p>
                <p className="text-xs text-yellow-600">{stats?.orders?.pending || 0} pending</p>
              </div>
              <ClipboardList className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">My Patients</p>
                <p className="text-2xl font-bold text-purple-600">{patients.length}</p>
              </div>
              <Users className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unread Messages</p>
                <p className="text-2xl font-bold text-amber-600">{stats?.messages?.unread || 0}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 flex-wrap">
        <Button onClick={() => navigate('/hospice-eligibility')} variant="outline" className="bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100">
          <Shield className="h-4 w-4 mr-2" />
          Eligibility Assessment
        </Button>
        <Dialog open={showNewReferral} onOpenChange={setShowNewReferral}>
          <DialogTrigger asChild>
            <Button data-testid="new-referral-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Referral
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Referral</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={newReferral.patient_first_name}
                    onChange={(e) => setNewReferral({...newReferral, patient_first_name: e.target.value})}
                    data-testid="referral-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={newReferral.patient_last_name}
                    onChange={(e) => setNewReferral({...newReferral, patient_last_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={newReferral.patient_dob}
                    onChange={(e) => setNewReferral({...newReferral, patient_dob: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newReferral.patient_phone}
                    onChange={(e) => setNewReferral({...newReferral, patient_phone: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Primary Diagnosis *</Label>
                <Input
                  value={newReferral.primary_diagnosis}
                  onChange={(e) => setNewReferral({...newReferral, primary_diagnosis: e.target.value})}
                  placeholder="e.g., End-stage heart failure"
                />
              </div>
              <div>
                <Label>Urgency</Label>
                <Select
                  value={newReferral.urgency}
                  onValueChange={(v) => setNewReferral({...newReferral, urgency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="emergent">Emergent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newReferral.notes}
                  onChange={(e) => setNewReferral({...newReferral, notes: e.target.value})}
                  placeholder="Additional clinical notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewReferral(false)}>Cancel</Button>
              <Button onClick={createReferral} data-testid="submit-referral-btn">
                <Send className="h-4 w-4 mr-2" />
                Submit Referral
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showNewOrder} onOpenChange={setShowNewOrder}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="new-order-btn">
              <ClipboardList className="h-4 w-4 mr-2" />
              New Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Physician Order</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Patient ID *</Label>
                  <Input
                    value={newOrder.patient_id}
                    onChange={(e) => setNewOrder({...newOrder, patient_id: e.target.value})}
                    data-testid="order-patient-id"
                  />
                </div>
                <div>
                  <Label>Patient Name</Label>
                  <Input
                    value={newOrder.patient_name}
                    onChange={(e) => setNewOrder({...newOrder, patient_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Order Type</Label>
                  <Select
                    value={newOrder.order_type}
                    onValueChange={(v) => setNewOrder({...newOrder, order_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="equipment">Equipment (DME)</SelectItem>
                      <SelectItem value="supplies">Supplies</SelectItem>
                      <SelectItem value="intervention">Intervention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={newOrder.priority}
                    onValueChange={(v) => setNewOrder({...newOrder, priority: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="stat">STAT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Instructions</Label>
                <Textarea
                  value={newOrder.instructions}
                  onChange={(e) => setNewOrder({...newOrder, instructions: e.target.value})}
                  placeholder="Order instructions..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewOrder(false)}>Cancel</Button>
              <Button onClick={createOrder}>Create Order</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Button variant="outline">
          <Video className="h-4 w-4 mr-2" />
          Schedule Telehealth
        </Button>

        <Button variant="outline">
          <MessageSquare className="h-4 w-4 mr-2" />
          Message Team
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="patients">My Patients</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Referrals */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.slice(0, 5).map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">
                        {referral.patient?.first_name} {referral.patient?.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{referral.referral_number}</p>
                    </div>
                    <Badge className={getStatusColor(referral.status)}>{referral.status}</Badge>
                  </div>
                ))}
                {referrals.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No referrals yet</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">{order.patient_name}</p>
                      <p className="text-xs text-gray-500">{order.order_number} - {order.order_type}</p>
                    </div>
                    <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                  </div>
                ))}
                {orders.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No orders yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <Card>
            <CardHeader>
              <CardTitle>My Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No referrals submitted yet</p>
                  <Button className="mt-4" onClick={() => setShowNewReferral(true)}>
                    Create First Referral
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {referrals.map((referral) => (
                    <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">
                          {referral.patient?.first_name} {referral.patient?.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{referral.referral_number}</p>
                        <p className="text-sm text-gray-500">
                          {referral.clinical?.primary_diagnosis}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(referral.status)}>{referral.status}</Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <CardTitle>Physician Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No orders created yet</p>
                  <Button className="mt-4" onClick={() => setShowNewOrder(true)}>
                    Create First Order
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{order.patient_name}</p>
                        <p className="text-sm text-gray-500">
                          {order.order_number} • {order.order_type}
                        </p>
                        {order.items?.length > 0 && (
                          <p className="text-xs text-gray-400">{order.items.length} item(s)</p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                        <Badge variant="outline" className="ml-2">{order.priority}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Patients Tab */}
        <TabsContent value="patients">
          <Card>
            <CardHeader>
              <CardTitle>My Referred Patients</CardTitle>
              <CardDescription>Patients you've referred to hospice care</CardDescription>
            </CardHeader>
            <CardContent>
              {patients.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No patients referred yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patients.map((patient) => (
                    <div
                      key={patient.referral_id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedPatient(patient);
                        if (patient.patient_id) {
                          fetchPatientInsights(patient.patient_id);
                        }
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <Users className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium">{patient.patient_name}</p>
                          <p className="text-sm text-gray-500">{patient.diagnosis}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(patient.status)}>{patient.status}</Badge>
                        {patient.patient_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              generateOrdersFromCarePlan(patient.patient_id);
                            }}
                          >
                            <Package className="h-4 w-4 mr-1" />
                            Generate Orders
                          </Button>
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Insights Panel */}
          {selectedPatient && patientInsights && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-600" />
                  AI Insights - {patientInsights.patient_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">7-Day Decline Risk</p>
                    <p className="text-2xl font-bold">
                      {patientInsights.insights?.decline_prediction?.["7_day"]?.risk_score || 0}%
                    </p>
                    <Badge className={getStatusColor(patientInsights.insights?.decline_prediction?.["7_day"]?.risk_level === 'high' ? 'rejected' : 'accepted')}>
                      {patientInsights.insights?.decline_prediction?.["7_day"]?.risk_level || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Hospitalization Risk</p>
                    <p className="text-2xl font-bold">
                      {patientInsights.insights?.hospitalization_risk?.risk_score || 0}%
                    </p>
                    <Badge className={getStatusColor(patientInsights.insights?.hospitalization_risk?.risk_level === 'high' ? 'rejected' : 'accepted')}>
                      {patientInsights.insights?.hospitalization_risk?.risk_level || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Care Intensity</p>
                    <p className="text-lg font-bold capitalize">
                      {patientInsights.insights?.care_intensity?.current_level || 'Standard'}
                    </p>
                  </div>
                </div>

                {patientInsights.insights?.alerts?.length > 0 && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-medium text-red-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Active Alerts
                    </p>
                    {patientInsights.insights.alerts.map((alert, idx) => (
                      <p key={idx} className="text-sm text-red-600 mt-1">{alert.message}</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle>Messages</CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No messages</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`p-4 border rounded-lg ${!msg.read ? 'bg-blue-50 border-blue-200' : ''}`}>
                      <div className="flex justify-between">
                        <p className="font-medium">{msg.sender_name}</p>
                        <span className="text-xs text-gray-500">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      {msg.subject && <p className="text-sm text-gray-700 mt-1">{msg.subject}</p>}
                      <p className="text-sm text-gray-600 mt-1">{msg.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PhysicianPortalEnhanced;
